<!--
  TimesEditor — a field's times as a list you edit in place: RowsEditor over a
  useTimesEditor, each line a TimeRowEditor when open.

  Lines are latest first. A closed line shows the time (with its kind's icon),
  what it was if an edit moved it, and a flag when two times are one instant
  written twice. An open line is the time's typed box with its inputs under
  it; the add line types a new one. Done emits `done` with the delta; Cancel
  emits `cancel`.
-->
<template>
  <RowsEditor
    :editor="editor"
    noun="time"
    :autofocus="autofocus"
    :footer-when-dirty="footerWhenDirty"
    @done="emit('done', $event)"
    @cancel="emit('cancel')"
  >
    <template #line="{ row }">
      <TimeElement :value="row.cur" show-kind />
      <span v-if="editor.wasText(row)" class="was">{{ editor.wasText(row) }}</span>
      <span v-else-if="row.cur.title" class="muted">{{ row.cur.title }}</span>
    </template>

    <template #badges="{ row }">
      <span
        v-if="editor.sameMoment.value.has(row.key)"
        class="badge is-warning"
        title="Same moment as another time"
      >
        same moment
      </span>
    </template>

    <template #open="{ row, close, remove, edited }">
      <TimeRowEditor
        :row="row.cur"
        :kind-editable="anyKind"
        @update="editor.setRow(row, $event)"
        @enter="close"
        @toggle="close"
      >
        <template #end>
          <span v-if="edited" class="badge is-edited">edited</span>
          <button type="button" class="remove" aria-label="Remove this time" @click="remove">
            <font-awesome-icon icon="xmark" />
          </button>
        </template>
      </TimeRowEditor>
    </template>

    <template #add="{ open, enter, toggle, autofocus: focusAdd }">
      <TimeRowEditor
        :key="editor.addCount.value"
        :row="editor.pending.value"
        :expanded="open"
        :kind-editable="anyKind"
        :placeholder="
          kind === 'instant'
            ? 'Add a time: nov 1, fri 5pm…'
            : 'Add a time: sep 14 1-2:30pm, apr 1-4…'
        "
        :seed="editor.addCount.value === 0 ? seed : ''"
        :autofocus="focusAdd"
        adding
        @update="editor.setPending"
        @enter="enter"
        @toggle="toggle"
      >
        <template #icon>
          <font-awesome-icon icon="plus" class="plus" fixed-width />
        </template>
        <template #end>
          <span v-if="editor.pendingGiven.value" class="enter">↵ adds</span>
        </template>
      </TimeRowEditor>
    </template>
  </RowsEditor>
</template>

<script setup lang="ts">
import { computed } from "vue"
import TimeElement from "@auger/ui/components/elements/TimeElement.vue"
import type { TimeKind, TimeRow } from "@auger/ui/utils/times"
import RowsEditor from "@/components/rows/RowsEditor.vue"
import { type TimesDelta, useTimesEditor } from "@/composables/useTimesEditor"
import TimeRowEditor from "./TimeRowEditor.vue"

const props = withDefaults(
  defineProps<{
    /** The field's rows as stored. */
    rows: TimeRow[]
    /** The kind a field holds, or null where any kind goes (aggregate Times). */
    kind?: TimeKind | null
    /** Typed text to start a new time with (a key pressed on the cell). */
    seed?: string
    /** Focus the add line on open — an overlay does, a form on a page doesn't. */
    autofocus?: boolean
    /** Show Cancel / Done only once something changed (a form at rest). */
    footerWhenDirty?: boolean
  }>(),
  { kind: null, seed: "", autofocus: true, footerWhenDirty: false }
)

const emit = defineEmits<{
  done: [delta: TimesDelta]
  cancel: []
}>()

const editor = useTimesEditor(props.rows, props.kind)

/** Any kind goes, so each time can change its kind. */
const anyKind = computed(() => props.kind == null)
</script>
